/**
 * @author    Odessa Team (odessateam@ab-soft.net)
 * @category  Ringcentral
 * @copyright Copyright (c) 2012-2026, RingCentral, Inc (http://www.ringcentral.com)
 */

/**
 * Typed shape of the BSS asset payload that the backend inlines into index.html as
 * `<script>window.__BSS__ = {...}</script>`. Every field is optional: a brand that
 * does not have BSS enabled (or has not yet been fetched) simply gets an empty
 * payload, and all callers fall back to their hardcoded defaults.
 */
export interface BssData
{
    brandName?: string;
    mainLogoUrl?: string;
    partnersLogoUrl?: string;
    supportLink?: string;
    tosLink?: string;
    legalLink?: string;
    copyright?: string;

    /** BSS-driven logo for the main header (BT and other brands that use theme-level asset) */
    serviceSiteMainLogo?: string;
    /** BSS-driven powered-by / secondary logo (e.g. BT footer) */
    logo_powered?: string;
    /** BSS-driven favicon URL */
    favicon?: string;
    /** Privacy Policy page URL */
    privacyPolicyPage?: string;
    /** Acceptable Use / Terms and Conditions page URL */
    acceptableUsePolicyPage?: string;
    /** Help / feedback / support page URL */
    feedbackPage?: string;
    /** Trademark notice text */
    notificationTradeMark?: string;
}

/**
 * Synchronous reader for BSS assets.
 *
 * The payload is produced by the backend cron (`sdb:bss-assets`) and injected into the
 * brand's index.html inside the `<!-- BSS:START --> ... <!-- BSS:END -->` block. By the
 * time this module is evaluated, the inline script has already been executed by the
 * browser — `window.__BSS__` is guaranteed to be populated (or absent, when BSS is off
 * for the brand).
 *
 * Because the data is read synchronously at construction time, there is no `init()`,
 * no `fetch`, no `localStorage`, and no flicker: Vue mounts with BSS values already
 * resolved.
 */
class BssService
{
    private readonly data: BssData;

    public constructor()
    {
        const w = window as unknown as { __BSS__?: BssData };
        this.data = w.__BSS__ ?? {};
    }

    /**
     * Returns true if the given key is present and is a non-empty string.
     */
    public has<K extends keyof BssData>(key: K): boolean
    {
        const v = this.data[key];
        return typeof v === "string" && v.length > 0;
    }

    /**
     * Returns the stored string value for the given key, or the provided fallback
     * when the key is absent / empty / of wrong type.
     */
    public get<K extends keyof BssData>(key: K, fallback: string): string
    {
        const v = this.data[key];
        return typeof v === "string" && v.length > 0 ? v : fallback;
    }
}

/**
 * Module-level singleton — shared across all entry files of the single Webpack bundle
 * (CoreServices*.ts, Header.ts, Footer.ts). Consumers simply import `bss` and call
 * `bss.has(...)` / `bss.get(...)` from Vue component getters.
 */
export const bss = new BssService();
